import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Alert,
  Autocomplete,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Paper,
  Snackbar,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { useHistory } from 'react-router-dom';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

// Icons
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import TableChartIcon from '@mui/icons-material/TableChart';
import HistoryIcon from '@mui/icons-material/History';

// Helpers / API
import { useAuth } from '../../Contexts/AuthContext';
import { useSimulation } from '../../Contexts/SimulationContext';
import {
  listBackupExports,
  deleteBackups,
  getOrganizationProjectsList,
  getUserProjectsList,
  getAccountsByGlKeys,
  getProjectAccountsMetadata,
  getAllChangesByProjectId,
} from '../../Helpers/dataApi';
import { exportProjectToExcel } from '../../Helpers/excelExport';
import { parseUtcTimestamp } from '../../Helpers/utils';

// Components
import Navigation from '../../Components/Navigation/navigation';
import SimulationBanner from '../../Components/SimulationBanner/SimulationBanner';
import ActionsCellRenderer from '../../Components/BackupActionsCallRenderer/backupActionsCallRenderer';

const mdTheme = createTheme();

// ─── File-name cell ─────────────────────────────────────────────────────────
const FileNameCellRenderer = (props) => {
  const full = props.data?.file_name ?? '';
  const noExt = full.replace(/\.(xlsx|csv)$/i, '');
  const display = noExt.length > 80 ? `${noExt.substring(0, 80)}…` : noExt;
  return (
    <Tooltip arrow placement="left" title={full}>
      <span>{display}</span>
    </Tooltip>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────
function formatDate(ds) {
  if (!ds) return '';
  const d = parseUtcTimestamp(ds);
  if (!d) return '';
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString()}`;
}
function formatFileSize(bytes) {
  if (!bytes || bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

function SectionHeader({ icon, title, subtitle }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
      <Box sx={{ color: 'primary.main', mt: 0.25 }}>{icon}</Box>
      <Box>
        <Typography variant="h6" fontWeight={600} lineHeight={1.2}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {subtitle}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
export default function BackupsAndExports() {
  const auth = useAuth();
  const simulation = useSimulation();
  const history = useHistory();
  const activeUser = auth.activeUser;
  const accessToken = activeUser.accessToken;

  // ── Project list for the export dropdown ─────────────────────────────────
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState(null);

  // ── Export state ──────────────────────────────────────────────────────────
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState('');

  // ── Export history grid ───────────────────────────────────────────────────
  const [exportItems, setExportItems] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [quickFilter, setQuickFilter] = useState('');
  const gridRef = useRef(null);

  // ── Delete confirmation dialog ────────────────────────────────────────────
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedExportId, setSelectedExportId] = useState(null);

  // ── Backdrop + snackbar ───────────────────────────────────────────────────
  const [backdrop, setBackdrop] = useState({ open: false, message: '' });
  const [snack, setSnack] = useState({ open: false, success: false, message: '' });
  const showSnack = (success, message) => setSnack({ open: true, success, message });

  // ── Load projects ─────────────────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      setProjectsLoading(true);
      try {
        const effectiveRole = simulation.getEffectiveRole();
        const effectiveOrg = simulation.getEffectiveOrganization();
        let list = [];
        if (effectiveRole === 0 || effectiveRole === 1) {
          const r = await getOrganizationProjectsList(effectiveOrg.id, accessToken);
          if (r?.status === 200) list = r.data ?? [];
        } else {
          const r = await getUserProjectsList(activeUser.id, effectiveOrg.id, accessToken);
          if (Array.isArray(r)) {
            r.forEach((item) => { if (item?.status === 200) list.push(item.data); });
          }
        }
        setProjects(list);
      } catch (e) {
        console.error('Failed to load projects', e);
      } finally {
        setProjectsLoading(false);
      }
    };
    load();
  }, [simulation.simulatedRole, simulation.simulatedOrganization]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Load export history ───────────────────────────────────────────────────
  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const r = await listBackupExports(accessToken);
      if (r?.status === 200) setExportItems(r.data ?? []);
    } catch (e) {
      console.error('Failed to load export history', e);
    } finally {
      setHistoryLoading(false);
    }
  }, [accessToken]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  // ── Quick filter ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (gridRef.current) setTimeout(() => gridRef.current.setQuickFilter(quickFilter), 0);
  }, [quickFilter]);

  // ── Export selected project to Excel ──────────────────────────────────────
  const handleExport = async () => {
    if (!selectedProject) return;
    setIsExporting(true);
    setExportError('');
    try {
      // Fetch metadata to get GL keys, then batch-fetch full account data with dates
      const metaResp = await getProjectAccountsMetadata(selectedProject.id, accessToken);
      const metaAccounts = metaResp?.data?.accounts ?? [];
      const glKeys = metaAccounts.map((a) => a.gl_key).filter(Boolean);

      let accountsWithDates = metaAccounts;
      if (glKeys.length > 0) {
        const BATCH = 5000;
        const fetched = [];
        for (let i = 0; i < glKeys.length; i += BATCH) {
          const r = await getAccountsByGlKeys(selectedProject.id, glKeys.slice(i, i + BATCH), accessToken);
          if (r?.status === 200) fetched.push(...(r.data?.accounts ?? []));
        }
        if (fetched.length > 0) accountsWithDates = fetched;
      }

      const changesResp = await getAllChangesByProjectId(selectedProject.id, accessToken);
      const changes = changesResp?.data ?? [];

      exportProjectToExcel({
        accounts: accountsWithDates,
        changes,
        projectTitle: selectedProject.title ?? `project_${selectedProject.id}`,
      });
    } catch (e) {
      console.error('Export failed', e);
      setExportError('Export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  // ── Grid row actions ──────────────────────────────────────────────────────
  const onDownload = useCallback((params) => {
    const url = params.data?.s3_url;
    if (url) window.open(url, '_blank');
  }, []);

  const onDelete = useCallback((params) => {
    setSelectedExportId(params.data?.id);
    setDeleteDialogOpen(true);
  }, []);

  const handleConfirmDelete = async () => {
    setDeleteDialogOpen(false);
    setBackdrop({ open: true, message: 'Deleting export record…' });
    try {
      const r = await deleteBackups([selectedExportId], accessToken);
      showSnack(r?.status === 200, r?.status === 200 ? 'Record deleted.' : 'Delete failed.');
    } catch {
      showSnack(false, 'An error occurred during delete.');
    } finally {
      setBackdrop({ open: false, message: '' });
      loadHistory();
    }
  };

  // ── Column defs ───────────────────────────────────────────────────────────
  const columnDefs = useMemo(() => [
    {
      headerName: 'File Name',
      field: 'file_name',
      sortable: true,
      filter: true,
      cellRenderer: FileNameCellRenderer,
      flex: 3,
    },
    {
      headerName: 'Project',
      field: 'project_title',
      sortable: true,
      filter: true,
      flex: 2,
    },
    {
      headerName: 'Exported By',
      field: 'project_created_by',
      sortable: true,
      filter: true,
      flex: 1,
    },
    {
      headerName: 'Size',
      field: 'file_size',
      sortable: true,
      filter: true,
      valueFormatter: (p) => formatFileSize(p.value),
      flex: 1,
      maxWidth: 100,
    },
    {
      headerName: 'Date',
      field: 'created_at',
      sortable: true,
      filter: true,
      sort: 'desc',
      valueFormatter: (p) => formatDate(p.value),
      flex: 2,
    },
    {
      headerName: 'Actions',
      cellRenderer: ActionsCellRenderer,
      cellRendererParams: { onDownload, onDelete },
      sortable: false,
      filter: false,
      flex: 1,
      maxWidth: 100,
    },
  ], [onDownload, onDelete]);

  const defaultColDef = useMemo(() => ({ resizable: true }), []);
  const onGridReady = useCallback((p) => { gridRef.current = p.api; }, []);

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <ThemeProvider theme={mdTheme}>
      <Box sx={{ display: 'flex' }}>
        <Navigation title="Backups & Exports" />

        <Box
          component="main"
          sx={{
            backgroundColor: (t) =>
              t.palette.mode === 'light' ? t.palette.grey[100] : t.palette.grey[900],
            flexGrow: 1,
            minHeight: '100vh',
            overflowX: 'hidden',
          }}
        >
          <Toolbar />
          <Box sx={{ px: 2, pt: 2 }}>
            <SimulationBanner />
          </Box>

          {/* Backdrop */}
          <Backdrop sx={{ color: '#fff', zIndex: (t) => t.zIndex.drawer + 1 }} open={backdrop.open}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
              <Typography sx={{ fontWeight: 'bold', fontSize: '1.2em' }}>{backdrop.message}</Typography>
              <CircularProgress size={80} color="inherit" />
            </Box>
          </Backdrop>

          {/* Snackbar */}
          <Snackbar
            open={snack.open}
            autoHideDuration={4000}
            onClose={() => setSnack((s) => ({ ...s, open: false }))}
          >
            <Alert
              severity={snack.success ? 'success' : 'error'}
              variant="filled"
              onClose={() => setSnack((s) => ({ ...s, open: false }))}
              sx={{ width: '100%' }}
            >
              {snack.message}
            </Alert>
          </Snackbar>

          {/* Delete confirmation dialog */}
          <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
            <DialogTitle>Delete export record?</DialogTitle>
            <DialogContent>
              <DialogContentText>
                This removes the record from the history table. The downloaded file on your machine is not affected.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
              <Button color="error" variant="contained" onClick={handleConfirmDelete}>Delete</Button>
            </DialogActions>
          </Dialog>

          {/* Page content */}
          <Box sx={{ px: 3, pt: 3, pb: 6, maxWidth: 1400, mx: 'auto' }}>

            {/* Section 1 — Export a Project */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <SectionHeader
                icon={<CloudDownloadIcon />}
                title="Export a Project"
                subtitle="Download a project as an Excel workbook (.xlsx). The file includes all accounts with monthly values, changes, and reference accounts — and is compatible with the New Project upload template for bulk editing and re-import."
              />
              <Divider sx={{ mb: 2.5 }} />

              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
                <Autocomplete
                  options={projects}
                  loading={projectsLoading}
                  getOptionLabel={(o) => o.title ?? ''}
                  value={selectedProject}
                  onChange={(_, v) => { setSelectedProject(v); setExportError(''); }}
                  isOptionEqualToValue={(o, v) => o.id === v?.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select project"
                      size="small"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {projectsLoading ? <CircularProgress size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  sx={{ minWidth: 320 }}
                />

                <Button
                  variant="contained"
                  startIcon={isExporting ? <CircularProgress size={16} color="inherit" /> : <TableChartIcon />}
                  onClick={handleExport}
                  disabled={!selectedProject || isExporting}
                  sx={{ height: 40 }}
                >
                  {isExporting ? 'Exporting…' : 'Export to Excel'}
                </Button>

                {selectedProject && (
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => history.push('/project-home')}
                    sx={{ height: 40 }}
                  >
                    Open project instead
                  </Button>
                )}
              </Box>

              {exportError && (
                <Alert severity="error" sx={{ mt: 2, maxWidth: 500 }}>
                  {exportError}
                </Alert>
              )}

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                To re-import an exported workbook as a new project, use the{' '}
                <Box
                  component="span"
                  sx={{ color: 'primary.main', cursor: 'pointer', textDecoration: 'underline' }}
                  onClick={() => history.push('/new-project')}
                >
                  New Project
                </Box>{' '}
                flow — it accepts the Account Data sheet directly.
              </Typography>
            </Paper>

            {/* Section 2 — Export History */}
            <Paper sx={{ p: 3 }}>
              <SectionHeader
                icon={<HistoryIcon />}
                title="Export History"
                subtitle="A log of all project exports for your organization. Download a previously exported file or remove the record."
              />
              <Divider sx={{ mb: 2 }} />

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <TextField
                  label="Search"
                  variant="outlined"
                  size="small"
                  value={quickFilter}
                  onChange={(e) => setQuickFilter(e.target.value)}
                  sx={{ minWidth: 240 }}
                />
                {historyLoading && <CircularProgress size={20} />}
              </Box>

              <div className="ag-theme-alpine" style={{ width: '100%', height: 460 }}>
                <AgGridReact
                  rowData={exportItems}
                  columnDefs={columnDefs}
                  defaultColDef={defaultColDef}
                  onGridReady={onGridReady}
                  pagination
                  paginationPageSize={15}
                  animateRows
                  overlayNoRowsTemplate={
                    historyLoading
                      ? '<span>Loading…</span>'
                      : '<span style="color:#888">No export history yet. Export a project above to get started.</span>'
                  }
                />
              </div>
            </Paper>
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
}
